/**
 * Sessão: o token, o painel e as chaves de acesso.
 *
 * O `painel_id` do legado É o usuário — o `LoginController` grava o cookie
 * `diagweb_painel` com ele, e o `painel.Acessar(chave)` decide o que aparece no
 * menu. Aqui a mesma ideia: o token carrega o painel, e as chaves vêm de
 * `GET api/acessos/meus`.
 *
 * O token mora em `localStorage` e vale 8 horas — um turno. Expirou, loga de
 * novo; não há refresh. A decisão está registrada no plano de execução: o elo
 * fraco hoje não é o transporte do token, é a senha, que o legado ainda compara
 * em texto plano dentro do SQL. Quando isso virar hash, vale reabrir a conversa
 * sobre cookie httpOnly.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { api, aoExpirarSessao, definirSessao, foraDoProjeto } from "@/api/cliente"
import type { AcessosResponse, LoginRequest, LoginResponse } from "@/api/tipos"

const CHAVE_ARMAZEM = "sistema-oc.sessao"

type SessaoGravada = { token: string; painelId: number; nome: string; expiraEm: number }

/**
 * As chaves que estão gravadas DENTRO do token.
 *
 * Não valida assinatura nem confia no conteúdo — quem valida é a API. Serve para
 * uma pergunta só: o que o token autoriza é o mesmo que o banco diz agora?
 */
function chavesDoToken(token: string): string[] {
  try {
    const corpo = token.split(".")[1]
    if (!corpo) return []
    const json = atob(corpo.replace(/-/g, "+").replace(/_/g, "/"))
    const dados = JSON.parse(decodeURIComponent(escape(json)))
    const acesso = dados.acesso
    return Array.isArray(acesso) ? acesso : acesso ? [acesso] : []
  } catch {
    return []
  }
}

function mesmasChaves(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false
  const conjunto = new Set(a)
  return b.every((x) => conjunto.has(x))
}

type Sessao = {
  autenticado: boolean
  carregando: boolean
  painelId: number | null
  nome: string
  /** Chaves do catálogo `Acessar` — as mesmas do `painel.Acessar(chave)` do legado. */
  chaves: ReadonlySet<string>
  pode: (chave: string) => boolean
  entrar: (dados: LoginRequest) => Promise<void>
  sair: () => void
}

const Contexto = createContext<Sessao | null>(null)

function ler(): SessaoGravada | null {
  try {
    const cru = localStorage.getItem(CHAVE_ARMAZEM)
    if (!cru) return null
    const s = JSON.parse(cru) as SessaoGravada
    if (!s.token || typeof s.expiraEm !== "number") return null
    if (Date.now() >= s.expiraEm) {
      localStorage.removeItem(CHAVE_ARMAZEM)
      return null
    }
    return s
  } catch {
    // Aba anônima, site data bloqueado, JSON corrompido: sem sessão e segue.
    return null
  }
}

function gravar(s: SessaoGravada | null) {
  try {
    if (s) localStorage.setItem(CHAVE_ARMAZEM, JSON.stringify(s))
    else localStorage.removeItem(CHAVE_ARMAZEM)
  } catch {
    /* sem armazenamento: a sessão vale só enquanto a aba estiver aberta */
  }
}

const OITO_HORAS = 8 * 60 * 60 * 1000

export function ProvedorDeSessao({ children }: { children: ReactNode }) {
  const [gravada, setGravada] = useState<SessaoGravada | null>(() => ler())
  const [chaves, setChaves] = useState<ReadonlySet<string>>(new Set())
  const [carregando, setCarregando] = useState(false)
  const qc = useQueryClient()

  // O cliente HTTP precisa saber do token antes de qualquer requisição sair.
  definirSessao(gravada?.token ?? null, gravada?.painelId ?? null)

  const sair = useCallback(() => {
    gravar(null)
    definirSessao(null, null)
    setGravada(null)
    setChaves(new Set())
    // O cache é da pessoa que saiu. Com cinco minutos de frescor e sem revalidar ao montar,
    // quem entrasse em seguida na mesma aba via as respostas dela — o "Só as minhas" das
    // Solicitações mostrava os pedidos do usuário anterior.
    qc.clear()
  }, [qc])

  useEffect(() => { aoExpirarSessao(sair) }, [sair])

  const entrar = useCallback(async (dados: LoginRequest) => {
    setCarregando(true)
    try {
      // Fora do projeto de propósito: o login é a única chamada que acontece
      // antes de existir projeto, e a trava do canário não tem o que conferir.
      const r = await api.post<LoginResponse>("api/login", dados, foraDoProjeto())
      const nova: SessaoGravada = {
        token: r.data.token,
        painelId: r.data.painelId,
        nome: r.data.nome,
        expiraEm: Date.now() + OITO_HORAS,
      }
      gravar(nova)
      definirSessao(nova.token, nova.painelId)
      // Mesmo motivo do `sair`, para quem entra sem ter saído (sessão expirada, outra conta).
      qc.clear()
      setGravada(nova)
    } finally {
      setCarregando(false)
    }
  }, [qc])

  // As chaves de acesso na fase 0, antes da primeira tela: menu, abas e ações
  // em lote se escondem por chave, então não dá para renderizar a casca sem elas.
  useEffect(() => {
    if (!gravada) return
    let vivo = true
    api
      .get<AcessosResponse>("api/acessos/meus", foraDoProjeto())
      .then(async (r) => {
        if (!vivo) return
        setChaves(new Set(r.data.chaves))

        // As chaves que a API AUTORIZA vêm de dentro do token e congelam até a
        // sessão virar; as que a tela MOSTRA vêm daqui, do banco, ao vivo. Quando
        // alguém mexe na permissão os dois discordam — e o efeito é o pior
        // possível: a seção aparece e a API recusa com 403. Se discordarem, um
        // token novo, com a mesma validade, põe os dois de acordo sem pedir senha.
        if (!mesmasChaves(chavesDoToken(gravada.token), r.data.chaves)) {
          try {
            const nova = await api.post<LoginResponse>("api/acessos/renovar", {}, foraDoProjeto())
            if (!vivo) return
            const atualizada: SessaoGravada = {
              token: nova.data.token,
              painelId: nova.data.painelId,
              nome: nova.data.nome,
              expiraEm: gravada.expiraEm,
            }
            gravar(atualizada)
            definirSessao(atualizada.token, atualizada.painelId)
            setGravada(atualizada)
          } catch {
            // Renovar é conforto, não requisito: falhou, a pessoa sai e entra de
            // novo como antes. Derrubar a sessão aqui seria pior que o problema.
          }
        }
      })
      .catch(() => { if (vivo) setChaves(new Set()) })
    return () => { vivo = false }
    // `gravada` basta: trocar o token dispara este efeito de novo, e da segunda vez
    // as duas listas já concordam.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gravada])

  const valor = useMemo<Sessao>(() => ({
    autenticado: gravada !== null,
    carregando,
    painelId: gravada?.painelId ?? null,
    nome: gravada?.nome ?? "",
    chaves,
    pode: (chave: string) => chaves.has(chave),
    entrar,
    sair,
  }), [gravada, carregando, chaves, entrar, sair])

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>
}

export function usarSessao(): Sessao {
  const s = useContext(Contexto)
  if (!s) throw new Error("usarSessao() fora do ProvedorDeSessao")
  return s
}

/** Esconde o que a chave não permite. Sem chave, sempre mostra. */
export function SePode({ chave, children }: { chave?: string; children: ReactNode }) {
  const { pode } = usarSessao()
  if (chave && !pode(chave)) return null
  return <>{children}</>
}
